import {
  Alert,
  Autocomplete,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  BulkUpdateConfirmationDefinition,
  ConstraintKey,
  getAttributeConfig,
  WriteStrategy,
} from '@provision/bulk-update-config';
import { ConversationStatus } from '@provision/conversation-types';
import { EntityBase } from '@provision/entity-types';
import { useCallback, useMemo, useState } from 'react';
import { isNullish } from 'remeda';
import { DynamicIcon } from 'src/components';
import {
  SearchDialog,
  type SearchDialogItem,
} from 'src/components/SearchDialog/search-dialog';
import { useAddress, useGroupRules, useServiceLevels } from 'src/graphql';
import { Address } from 'src/graphql/entities/address/address';
import { getCorrectSubscriptionIdFromAddress } from 'src/graphql/entities/address/utils';
import {
  BulkUpdateSelectionInput,
  useBulkUpdateEntitiesMutation,
} from 'src/graphql/entities/custom-view/custom-view-bulk-update';
import {
  type BulkCopyActionDefinition,
  type BulkCopyContext,
  type BulkCopyEntity,
  formatBulkCopyLinks,
  formatBulkCopyValues,
  getBulkCopyActions,
} from 'src/graphql/entities/custom-view/custom-view-config/bulk-copy-config';
import {
  getBulkUpdateActions,
  mapCustomViewEntityToEntityName,
} from 'src/graphql/entities/custom-view/custom-view-config/bulk-update-config';
import { CustomViewEntity } from 'src/graphql/entities/custom-view/custom-view-config/custom-view-config';
import { getBulkEditorCapabilities } from 'src/pages/CustomViews/components/bulk-actions-modal.editor-config';
import { useBulkActionOptions } from 'src/pages/CustomViews/components/bulk-actions-modal.options';
import { useBulkActionsModalState } from 'src/pages/CustomViews/components/bulk-actions-modal.state';
import { useBulkActionSubmit } from 'src/pages/CustomViews/components/bulk-actions-modal.submit';
import {
  BulkOption,
  getBulkOptionKey,
  getBulkOptionSubmitValue,
} from 'src/pages/CustomViews/components/bulk-actions-modal.utils';
import {
  hasBulkUpdateActionAccess,
  shouldShowBulkUpdateAction,
} from 'src/pages/CustomViews/utils';
import {
  SupportedActionQueries,
  useOptionsQueries,
} from 'src/pages/Settings/rules/use-rules-engine';
import { useCopyToClipboard, useNotifications } from 'src/utils/hooks';
import { useListKeyboardNavigation } from 'src/utils/hooks/use-list-keyboard-navigation';

interface BulkActionsModalProps {
  open: boolean;
  onClose: () => void;
  onBulkUpdateSubmitted: (jobId: string, operationKind?: 'update' | 'delete') => void;
  onBulkActionCompleted?: () => void;
  entity: CustomViewEntity;
  records: EntityBase[];
  totalCount: number;
  selectedCount: number;
  partnerId: number | null;
  selectionInput: BulkUpdateSelectionInput;
  getEntityUrl: (entityId: string) => string;
  asyncActions?: BulkActionsModalAction[];
  initialUpdateAttribute?: string | null;
  canBulkEdit?: boolean;
  canBulkDelete?: boolean;
}

export type BulkActionsModalAction = {
  id: string;
  label: string;
  icon: string;
  handler: () => Promise<void> | void;
  isLoading?: boolean;
  isAllowed?: boolean;
};

type BulkModalAction =
  | {
      id: string;
      label: string;
      icon: string;
      isLoading?: boolean;
      kind: 'copy';
      copyAction: BulkCopyActionDefinition;
    }
  | {
      id: string;
      label: string;
      icon: string;
      isLoading?: boolean;
      kind: 'update-attribute';
      attributeKey: string;
    }
  | {
      id: string;
      label: string;
      icon: string;
      isLoading?: boolean;
      kind: 'delete';
      attributeKey: string;
    }
  | {
      id: string;
      label: string;
      icon: string;
      kind: 'async';
      isLoading: boolean;
      handler: () => Promise<void> | void;
    };

type BulkActionSearchItem = SearchDialogItem & { originalAction: BulkModalAction };

const BULK_MODAL_MAX_HEIGHT = '50vh';

const EMPTY_ASYNC_ACTIONS: BulkActionsModalAction[] = [];

const BOOLEAN_TRUE_VALUES = new Set(['yes', 'true', '1']);
const BOOLEAN_FALSE_VALUES = new Set(['no', 'false', '0']);

// temp solution, will be reworked soon
const ADDRESS_BOOLEAN_STRING_ATTRIBUTES = new Set(['tosAccepted', 'creditCardProvided']);

const OPTION_ICONS: Record<string, string> = {
  yes: 'check',
  no: 'close',
};

export const BulkActionsModal = ({
  open,
  onClose,
  onBulkUpdateSubmitted,
  onBulkActionCompleted,
  entity,
  records,
  totalCount,
  selectedCount,
  partnerId,
  selectionInput,
  getEntityUrl,
  asyncActions = EMPTY_ASYNC_ACTIONS,
  initialUpdateAttribute = null,
  canBulkEdit = true,
  canBulkDelete = false,
}: BulkActionsModalProps) => {
  const [pendingConfirmation, setPendingConfirmation] = useState<
    | (BulkUpdateConfirmationDefinition & {
        onConfirm: () => Promise<void> | void;
      })
    | null
  >(null);

  const { copyToClipboard } = useCopyToClipboard({ alertOptions: { autoHide: 1000 } });
  const copyContext = useMemo<BulkCopyContext>(() => ({ getEntityUrl }), [getEntityUrl]);
  const { showErrorAlert, showSuccessAlert } = useNotifications();

  const { groupRules } = useGroupRules();
  const [initBulkUpdate, { loading: isSubmitInProgress }] = useBulkUpdateEntitiesMutation();
  const { associateClientsWithAddress } = useAddress();

  const bulkCopyActions = useMemo(
    () => getBulkCopyActions(entity as BulkCopyEntity),
    [entity],
  );

  const entityName = useMemo(() => mapCustomViewEntityToEntityName(entity), [entity]);

  const bulkUpdateActions = useMemo(
    () => getBulkUpdateActions(entity, { isMultitenancy: isNullish(partnerId) }),
    [entity, partnerId],
  );
  const availableBulkUpdateAttributes = useMemo(
    () => bulkUpdateActions.map((action) => action.attributeKey),
    [bulkUpdateActions],
  );
  const {
    step,
    selectedAttribute,
    editorValue,
    setEditorValue,
    multiSelectValues,
    setMultiSelectValues,
    searchInputValue,
    setSearchInputValue,
    optionSearchValue,
    setOptionSearchValue,
    snoozeUntilValue,
    setSnoozeUntilValue,
    openUpdateConfig,
    resetToActions,
  } = useBulkActionsModalState({
    open,
    initialUpdateAttribute,
    availableAttributes: availableBulkUpdateAttributes,
  });

  const enabledQueries = useMemo(() => {
    return new Set(
      bulkUpdateActions
        .map((action): SupportedActionQueries | undefined => action.optionsQuery)
        .filter(Boolean) as SupportedActionQueries[],
    );
  }, [bulkUpdateActions]);

  const queryOptions = useOptionsQueries({
    partnerId,
    enabledQueries,
    area: 'custom_views',
    concreteCurrentUserOption: true,
  });

  const { serviceLevelOptions, hasServiceLevelAccess } = useServiceLevels(partnerId);

  const actions = useMemo<BulkModalAction[]>(() => {
    const copyActions = bulkCopyActions.map(
      (action): BulkModalAction => ({
        id: action.id,
        label: action.label,
        icon: action.icon,
        isLoading: false,
        kind: 'copy' as const,
        copyAction: action,
      }),
    );

    const updateActions = canBulkEdit
      ? bulkUpdateActions
          .filter(
            (action) =>
              action.kind !== 'delete' &&
              shouldShowBulkUpdateAction(action, groupRules) &&
              (hasServiceLevelAccess ||
                getAttributeConfig(entityName, action.attributeKey)?.validation
                  .constraintKey !== ConstraintKey.ServiceLevelBrand),
          )
          .map(
            (action): BulkModalAction => ({
              id: action.id,
              label: action.label,
              icon: action.icon,
              isLoading: false,
              kind: 'update-attribute',
              attributeKey: action.attributeKey,
            }),
          )
      : [];

    const deleteActions = canBulkDelete
      ? bulkUpdateActions
          .filter(
            (action) =>
              action.kind === 'delete' && hasBulkUpdateActionAccess(action, groupRules),
          )
          .map(
            (action): BulkModalAction => ({
              id: action.id,
              label: action.label,
              icon: action.icon,
              isLoading: false,
              kind: 'delete',
              attributeKey: action.attributeKey,
            }),
          )
      : [];

    const asyncActionsList =
      asyncActions
        ?.filter((action) => action.isAllowed)
        .map(
          (action): BulkModalAction => ({
            id: action.id,
            label: action.label,
            icon: action.icon,
            kind: 'async',
            isLoading: Boolean(action.isLoading),
            handler: action.handler,
          }),
        ) ?? [];

    return [...copyActions, ...updateActions, ...asyncActionsList, ...deleteActions];
  }, [
    bulkCopyActions,
    bulkUpdateActions,
    asyncActions,
    canBulkEdit,
    canBulkDelete,
    groupRules,
    hasServiceLevelAccess,
    entityName,
  ]);

  const searchDialogActions = useMemo<BulkActionSearchItem[]>(
    () =>
      actions.map((action) => ({
        id: action.id,
        title: action.label,
        description: `${selectedCount} items`,
        icon: (
          <Stack direction="row" alignItems="center" sx={{ mr: 2 }}>
            {action.isLoading ? (
              <CircularProgress size={20} />
            ) : (
              <DynamicIcon name={action.icon} fontSize="small" />
            )}
          </Stack>
        ),
        keywords: [action.label],
        originalAction: action,
      })),
    [actions, selectedCount],
  );

  const bulkActionFilterHandler = useCallback(
    (_: string, search = '', keywords: string[] = []) => {
      if (!search.trim()) return 1;
      const term = search.trim().toLowerCase();
      return keywords.some((k) => k.toLowerCase().includes(term)) ? 1 : 0;
    },
    [],
  );

  const selectedBulkAction = useMemo(
    () => bulkUpdateActions.find((action) => action.attributeKey === selectedAttribute),
    [bulkUpdateActions, selectedAttribute],
  );
  const isDeleteAction = selectedBulkAction?.kind === 'delete';

  const selectedAttrConfig = useMemo(() => {
    if (!selectedBulkAction?.updateAttribute) return undefined;
    return getAttributeConfig(entityName, selectedBulkAction.updateAttribute);
  }, [entityName, selectedBulkAction?.updateAttribute]);

  const isLabelMutationAction =
    selectedAttrConfig?.execution.writeStrategy === WriteStrategy.LabelMutation;

  const isServiceLevelBulkUpdate = useMemo(
    () => selectedAttrConfig?.validation.constraintKey === ConstraintKey.ServiceLevelBrand,
    [selectedAttrConfig],
  );

  const selectedSiteBrandIds = useMemo(() => {
    if (!isServiceLevelBulkUpdate || entityName !== 'Address') {
      return [];
    }

    return [
      ...new Set(records.map((record) => String((record as Partial<Address>)?.brandId || ''))),
    ].filter(Boolean);
  }, [entityName, isServiceLevelBulkUpdate, records]);

  const {
    selectedBulkOptions,
    selectedBulkQueryValue,
    selectedBulkAutocompleteOptions,
    selectedEditorOption,
    filteredBulkOptions,
  } = useBulkActionOptions({
    entityName,
    records,
    selectedBulkAction,
    selectedAttrConfig,
    queryOptions: queryOptions as Record<
      string,
      {
        options?: BulkOption[];
        historicalOptions?: BulkOption[];
        isLoading?: boolean;
        onInputChange?: (value: string) => void;
      }
    >,
    groupRules,
    isServiceLevelBulkUpdate,
    constrainedServiceLevelOptions: serviceLevelOptions as BulkOption[],
    partnerId,
    editorValue,
    optionSearchValue,
  });

  const anySelectedSiteHasSubscription = useMemo(() => {
    if (entityName !== 'Address') {
      return false;
    }

    return records.some((record) =>
      Boolean(getCorrectSubscriptionIdFromAddress(record as Address)),
    );
  }, [entityName, records]);

  const hasSubscriptionInSelectedSites =
    isServiceLevelBulkUpdate && anySelectedSiteHasSubscription;

  const requiresSnoozeUntilForValue = useCallback(
    (value: string): boolean => {
      const hasSnoozeMetadata =
        selectedAttrConfig?.execution.metadata?.fields.includes('snoozeUntil');

      return Boolean(
        hasSnoozeMetadata && value.toLowerCase() === ConversationStatus.Snoozed.toLowerCase(),
      );
    },
    [selectedAttrConfig],
  );

  const requiresSnoozeUntil = useMemo(
    () => requiresSnoozeUntilForValue(editorValue),
    [editorValue, requiresSnoozeUntilForValue],
  );

  const handleClose = useCallback(() => {
    setPendingConfirmation(null);
    resetToActions();
    onClose();
  }, [onClose, resetToActions]);

  const normalizeEditorValue = useCallback(
    (rawValue: string): string | boolean => {
      if (selectedBulkAction?.editorType === 'boolean') {
        const normalized = rawValue.trim().toLowerCase();

        const needsYesNoString =
          entityName === 'Address' &&
          ADDRESS_BOOLEAN_STRING_ATTRIBUTES.has(
            String(selectedBulkAction.updateAttribute ?? ''),
          );

        let normalizedAsYesNo: 'yes' | 'no';
        if (BOOLEAN_TRUE_VALUES.has(normalized)) {
          normalizedAsYesNo = 'yes';
        } else if (BOOLEAN_FALSE_VALUES.has(normalized)) {
          normalizedAsYesNo = 'no';
        } else {
          normalizedAsYesNo = rawValue === 'Yes' ? 'yes' : 'no';
        }

        if (needsYesNoString) {
          return normalizedAsYesNo;
        }

        return normalizedAsYesNo === 'yes';
      }

      if (selectedBulkAction?.editorType === 'entity-search') {
        const normalizedRawValue = String(rawValue);
        const matchingOption = selectedBulkAutocompleteOptions.find(
          (option) =>
            getBulkOptionSubmitValue(option, selectedBulkAction?.editorType) ===
            normalizedRawValue,
        );

        if (selectedAttrConfig?.editor.optionsSource === 'getConversationParticipantOptions') {
          return String(
            matchingOption?.conversationParticipantId ?? matchingOption?.id ?? rawValue,
          );
        }

        return String(matchingOption?.id ?? rawValue);
      }
      if (selectedBulkAction?.editorType === 'date' && rawValue) {
        return rawValue;
      }

      if (
        entityName === 'Address' &&
        String(selectedBulkAction?.updateAttribute ?? '') === 'clientActivatedMembership'
      ) {
        return rawValue.trim();
      }

      return rawValue;
    },
    [
      entityName,
      selectedAttrConfig,
      selectedBulkAction?.editorType,
      selectedBulkAction?.updateAttribute,
      selectedBulkAutocompleteOptions,
    ],
  );

  const { handleSubmitBulkUpdate, handleSubmitBulkDelete } = useBulkActionSubmit({
    isSubmitInProgress,
    selectedBulkAction,
    selectedAttrConfig,
    selectedBulkOptions,
    editorValue,
    multiSelectValues,
    snoozeUntilValue,
    requiresSnoozeUntilForValue,
    isLabelMutationAction,
    selectedCount,
    isServiceLevelBulkUpdate,
    selectedSiteBrandIds,
    hasSubscriptionInSelectedSites,
    records,
    associateClientsWithAddress,
    normalizeEditorValue,
    initBulkUpdate: initBulkUpdate as unknown as (
      args: unknown,
    ) => Promise<{ data?: { bulkUpdateEntities?: { jobId?: string } } }>,
    partnerId,
    entityName,
    selectionInput,
    showErrorAlert,
    showSuccessAlert,
    onBulkActionCompleted,
    onBulkUpdateSubmitted,
    handleClose,
  });

  const submitBulkDeleteActionWithConfirmation = useCallback(
    async (attributeKey: string) => {
      const deleteAction = bulkUpdateActions.find(
        (action) => action.kind === 'delete' && action.attributeKey === attributeKey,
      );
      if (!deleteAction) {
        return;
      }

      if (anySelectedSiteHasSubscription) {
        showErrorAlert(
          "Some selected sites have an active subscription and can't be deleted. Remove them from the selection (or cancel the subscription in billing) and try again.",
        );
        return;
      }

      const confirmation = deleteAction.confirmation;

      if (!confirmation?.title) {
        await handleSubmitBulkDelete({ attributeKey });
        return;
      }

      setPendingConfirmation({
        ...confirmation,
        confirmButtonColor: 'error',
        onConfirm: () =>
          handleSubmitBulkDelete({
            attributeKey,
          }),
      });
    },
    [
      bulkUpdateActions,
      handleSubmitBulkDelete,
      anySelectedSiteHasSubscription,
      showErrorAlert,
    ],
  );

  const submitBulkUpdateWithConfirmation = useCallback(
    async ({
      overrideEditorValue,
      overrideSnoozeUntilValue,
      overrideMultiSelectValues,
    }: {
      overrideEditorValue?: string;
      overrideSnoozeUntilValue?: string;
      overrideMultiSelectValues?: string[];
    } = {}) => {
      if (!selectedBulkAction || selectedBulkAction.kind !== 'update') {
        return;
      }

      const confirmation = selectedBulkAction.confirmation;

      if (!confirmation?.title) {
        await handleSubmitBulkUpdate({
          overrideEditorValue,
          overrideSnoozeUntilValue,
          overrideMultiSelectValues,
        });
        return;
      }

      setPendingConfirmation({
        ...confirmation,
        confirmButtonColor: 'primary',
        onConfirm: () =>
          handleSubmitBulkUpdate({
            overrideEditorValue,
            overrideSnoozeUntilValue,
            overrideMultiSelectValues,
          }),
      });
    },
    [handleSubmitBulkUpdate, setPendingConfirmation, selectedBulkAction],
  );

  const submitBulkDeleteWithConfirmation = useCallback(async () => {
    if (!selectedBulkAction || selectedBulkAction.kind !== 'delete') {
      return;
    }
    await submitBulkDeleteActionWithConfirmation(selectedBulkAction.attributeKey);
  }, [selectedBulkAction, submitBulkDeleteActionWithConfirmation]);

  const handleActionClick = useCallback(
    async (action: BulkModalAction) => {
      if (action.kind === 'update-attribute') {
        openUpdateConfig(action.attributeKey);
        return;
      }

      if (action.kind === 'delete') {
        await submitBulkDeleteActionWithConfirmation(action.attributeKey);
        return;
      }

      if (action.kind === 'async') {
        await action.handler();
        onClose();
        return;
      }

      const copyAction = action.copyAction;
      const values = records.map((record) => copyAction.resolver(record, copyContext));

      if (copyAction.isFriendlyLink) {
        await copyToClipboard(formatBulkCopyLinks(values));
      } else {
        await copyToClipboard(formatBulkCopyValues(values));
      }

      onClose();
    },
    [
      copyContext,
      copyToClipboard,
      onClose,
      openUpdateConfig,
      records,
      submitBulkDeleteActionWithConfirmation,
    ],
  );

  const handleOptionClick = useCallback(
    async (option: BulkOption) => {
      if (!selectedBulkAction || isSubmitInProgress) {
        return;
      }

      const nextEditorValue = getBulkOptionSubmitValue(option, selectedBulkAction.editorType);
      const isMultiSelectEditor = selectedBulkAction.editorType === 'multi-select';

      if (isMultiSelectEditor) {
        setMultiSelectValues((current) =>
          current.includes(nextEditorValue)
            ? current.filter((value) => value !== nextEditorValue)
            : [...current, nextEditorValue],
        );
        return;
      }

      setEditorValue(nextEditorValue);

      if (requiresSnoozeUntilForValue(nextEditorValue)) {
        setSnoozeUntilValue('');
        return;
      }

      await submitBulkUpdateWithConfirmation({ overrideEditorValue: nextEditorValue });
    },
    [
      selectedBulkAction,
      isSubmitInProgress,
      requiresSnoozeUntilForValue,
      submitBulkUpdateWithConfirmation,
      setEditorValue,
      setMultiSelectValues,
      setSnoozeUntilValue,
    ],
  );

  const editorCapabilities = useMemo(
    () => getBulkEditorCapabilities(selectedBulkAction?.editorType),
    [selectedBulkAction?.editorType],
  );
  const isOptionListEditor = editorCapabilities.isOptionList;
  const needsManualSubmit = editorCapabilities.needsManualSubmit || requiresSnoozeUntil;

  const isSaveDisabled = useMemo(() => {
    let isValueMissing = false;
    if (!isDeleteAction) {
      isValueMissing =
        selectedBulkAction?.editorType === 'multi-select'
          ? multiSelectValues.length === 0
          : !editorValue;
    }

    return (
      !selectedBulkAction ||
      isValueMissing ||
      (requiresSnoozeUntil && !snoozeUntilValue) ||
      isSubmitInProgress
    );
  }, [
    isDeleteAction,
    selectedBulkAction,
    editorValue,
    multiSelectValues,
    requiresSnoozeUntil,
    snoozeUntilValue,
    isSubmitInProgress,
  ]);
  let saveButtonLabel = 'Save';
  if (isDeleteAction) {
    saveButtonLabel = 'Continue';
  }
  if (isSubmitInProgress) {
    saveButtonLabel = 'Submitting...';
  }

  const getOptionIcon = useCallback((option: BulkOption): string => {
    if (option.icon) {
      return option.icon;
    }

    const normalizedOptionValue = String(option.value ?? option.id ?? '').toLowerCase();
    return OPTION_ICONS[normalizedOptionValue] ?? '';
  }, []);

  const getTitleByStep = useCallback(
    (step: 'actions' | 'update-config'): string => {
      if (step === 'actions') {
        return 'Bulk actions';
      }

      if (step === 'update-config') {
        if (selectedBulkAction?.kind === 'delete') {
          return `Bulk delete ${selectedBulkAction?.attributeLabel}`;
        }
        return `Bulk update ${selectedBulkAction?.attributeLabel}`;
      }

      return 'Bulk update';
    },
    [selectedBulkAction?.attributeLabel, selectedBulkAction?.kind],
  );

  const {
    setSelectedIndex: setOptionListSelectedIndex,
    registerItemRef: registerOptionListItemRef,
    handleKeyDown: handleOptionListKeyDown,
  } = useListKeyboardNavigation({
    itemCount: isOptionListEditor ? filteredBulkOptions.length : 0,
    wrap: true,
    initialIndex: -1,
    focusSelectedItem: true,
  });

  const handleSearchDialogSelect = useCallback(
    (item: BulkActionSearchItem) => {
      void handleActionClick(item.originalAction);
    },
    [handleActionClick],
  );

  return (
    <>
      {open && step === 'actions' && !isDeleteAction ? (
        <SearchDialog
          options={searchDialogActions}
          onClose={handleClose}
          placeholder="Type to filter..."
          onSelect={handleSearchDialogSelect}
          filterHandler={bulkActionFilterHandler}
          endAdornment={false}
        />
      ) : null}

      <Dialog
        open={Boolean(pendingConfirmation)}
        onClose={() => (isSubmitInProgress ? undefined : setPendingConfirmation(null))}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{pendingConfirmation?.title}</DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            <Typography variant="body2">
              {pendingConfirmation?.getDescription(selectedCount, totalCount)}
            </Typography>
            {pendingConfirmation?.warning ? (
              <Alert severity="warning">{pendingConfirmation?.warning}</Alert>
            ) : null}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            variant="text"
            onClick={() => setPendingConfirmation(null)}
            disabled={isSubmitInProgress}
          >
            {pendingConfirmation?.cancelButtonTitle ?? 'Cancel'}
          </Button>
          <Button
            variant="contained"
            color={pendingConfirmation?.confirmButtonColor ?? 'primary'}
            onClick={async () => {
              if (!pendingConfirmation) return;
              const { onConfirm } = pendingConfirmation;
              try {
                await onConfirm();
              } finally {
                setPendingConfirmation(null);
              }
            }}
            disabled={isSubmitInProgress}
          >
            {isSubmitInProgress
              ? 'Submitting...'
              : (pendingConfirmation?.confirmButtonTitle ?? 'Confirm')}
          </Button>
        </DialogActions>
      </Dialog>

      {step === 'update-config' ? (
        <Dialog
          open={open}
          onClose={handleClose}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              maxHeight: BULK_MODAL_MAX_HEIGHT,
            },
          }}
        >
          <DialogTitle>
            <Typography variant="overline" color="text.secondary">
              {getTitleByStep(step)}
            </Typography>
          </DialogTitle>
          <DialogContent
            sx={{
              pt: 1,
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              overflow: 'hidden',
              minHeight: 0,
            }}
          >
            <Stack
              spacing={2}
              sx={{
                overflowY: isOptionListEditor ? 'hidden' : 'auto',
                minHeight: 0,
                flex: 1,
              }}
            >
              <Typography variant="body2" color="text.secondary">
                {selectedCount} / {totalCount} items selected
              </Typography>

              {editorCapabilities.renderTextInput && !isDeleteAction ? (
                <TextField
                  fullWidth
                  size="small"
                  label="Value"
                  autoFocus
                  value={editorValue}
                  onChange={(event) => setEditorValue(event.target.value)}
                />
              ) : null}

              {editorCapabilities.renderDateInput && !isDeleteAction ? (
                <TextField
                  fullWidth
                  size="small"
                  label="Value"
                  type="date"
                  autoFocus
                  value={editorValue}
                  onChange={(event) => setEditorValue(event.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              ) : null}

              {editorCapabilities.renderEntitySearch && !isDeleteAction ? (
                <Stack spacing={1}>
                  <Autocomplete
                    fullWidth
                    size="small"
                    options={selectedBulkAutocompleteOptions}
                    value={selectedEditorOption}
                    loading={Boolean(selectedBulkQueryValue?.isLoading)}
                    inputValue={searchInputValue}
                    isOptionEqualToValue={(option, value) =>
                      getBulkOptionKey(option) === getBulkOptionKey(value)
                    }
                    getOptionLabel={(option) =>
                      option.label ?? String(option.value ?? option.id ?? '')
                    }
                    onInputChange={(_event, value, reason) => {
                      setSearchInputValue(value);
                      if (reason === 'input') {
                        selectedBulkQueryValue?.onInputChange?.(value);
                      }
                    }}
                    onChange={(_event, option) => {
                      if (!option) {
                        setEditorValue('');
                        return;
                      }
                      setEditorValue(
                        getBulkOptionSubmitValue(option, selectedBulkAction?.editorType),
                      );
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        autoFocus
                        label="Value"
                        placeholder="Search options"
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {selectedBulkQueryValue?.isLoading ? (
                                <CircularProgress color="inherit" size={16} />
                              ) : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                  />
                </Stack>
              ) : null}

              {selectedBulkAction && isOptionListEditor && !isDeleteAction ? (
                <Stack sx={{ minHeight: 0, flex: 1 }}>
                  <TextField
                    fullWidth
                    size="small"
                    autoFocus
                    placeholder="Type to filter..."
                    onKeyDown={(event) => {
                      const handled = handleOptionListKeyDown(event);
                      if (handled) {
                        event.stopPropagation();
                      }
                    }}
                    value={optionSearchValue}
                    onChange={(event) => {
                      setOptionSearchValue(event.target.value);
                      setOptionListSelectedIndex(-1);
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <DynamicIcon
                            name="search"
                            sx={{ width: '1.25rem', height: '1.25rem' }}
                          />
                        </InputAdornment>
                      ),
                    }}
                    sx={{ flexShrink: 0 }}
                  />
                  <List dense disablePadding sx={{ overflowY: 'auto', mt: 1 }}>
                    {filteredBulkOptions.map((option, index) => {
                      const optionValue = getBulkOptionSubmitValue(
                        option,
                        selectedBulkAction?.editorType,
                      );
                      const isSelected =
                        selectedBulkAction?.editorType === 'multi-select'
                          ? multiSelectValues.includes(optionValue)
                          : editorValue === optionValue;

                      return (
                        <ListItemButton
                          key={String(option.id ?? option.value)}
                          selected={isSelected}
                          disabled={isSubmitInProgress}
                          onKeyDown={(event) => {
                            const handled = handleOptionListKeyDown(event);
                            if (handled) {
                              event.stopPropagation();
                            }
                          }}
                          onFocus={() => setOptionListSelectedIndex(index)}
                          onMouseEnter={() => setOptionListSelectedIndex(index)}
                          ref={registerOptionListItemRef(index)}
                          onClick={() => void handleOptionClick(option)}
                          sx={{
                            borderRadius: 1,
                            px: 1,
                          }}
                        >
                          {!!getOptionIcon(option) && (
                            <ListItemIcon sx={{ minWidth: 36 }}>
                              <DynamicIcon
                                name={getOptionIcon(option)}
                                fontSize="small"
                                sx={option.color ? { color: option.color } : undefined}
                              />
                            </ListItemIcon>
                          )}
                          <ListItemText
                            primary={option.label ?? String(option.value ?? option.id ?? '')}
                          />
                        </ListItemButton>
                      );
                    })}
                    {filteredBulkOptions.length === 0 ? (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ px: 2, py: 1.5 }}
                      >
                        No matching options
                      </Typography>
                    ) : null}
                  </List>
                </Stack>
              ) : null}

              {requiresSnoozeUntil ? (
                <TextField
                  fullWidth
                  size="small"
                  type="datetime-local"
                  label="Snooze Until"
                  autoFocus
                  value={snoozeUntilValue}
                  onChange={(event) => setSnoozeUntilValue(event.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              ) : null}
            </Stack>

            <Stack direction="row" spacing={1} justifyContent="space-between">
              <Button variant="text" onClick={resetToActions}>
                Back
              </Button>
              {needsManualSubmit ? (
                <Button
                  variant="contained"
                  onClick={() =>
                    void (isDeleteAction
                      ? submitBulkDeleteWithConfirmation()
                      : submitBulkUpdateWithConfirmation())
                  }
                  disabled={isSaveDisabled}
                >
                  {saveButtonLabel}
                </Button>
              ) : null}
            </Stack>
          </DialogContent>
        </Dialog>
      ) : null}
    </>
  );
};
